import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf, timeAgo, fdateShort } from "@/lib/format";
import { Loader, StatCard } from "@/components/Common";
import { PremiumBanner } from "@/components/PremiumGate";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users, Wallet, ListChecks, Armchair, Sparkles, CalendarClock, TrendingUp, Lightbulb, Store,
  Check, Circle, ArrowRight,
} from "lucide-react";

export default function Dashboard() {
  const { wedding, user } = useAuth();
  const wid = wedding?.wedding?.id;
  const [d, setD] = useState(null);
  const [activity, setActivity] = useState([]);
  const [ob, setOb] = useState(null);

  useEffect(() => {
    if (!wid) return;
    api.get(`/weddings/${wid}/dashboard`).then(({ data }) => setD(data));
    api.get(`/weddings/${wid}/activity`).then(({ data }) => setActivity(data));
    api.get(`/weddings/${wid}/onboarding`).then(({ data }) => setOb(data)).catch(() => {});
  }, [wid]);

  if (!d) return <Loader />;
  const w = d.wedding;
  const cur = d.budget.currency;

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <div className="eyebrow mb-2">Tableau de bord</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-stone-900">{w.names}</h1>
          <p className="text-stone-500 mt-2 flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            {d.days_left != null ? `J-${d.days_left} avant votre mariage` : "Date à définir"} · {w.city}</p>
        </div>
        <div className="card-lux px-6 py-4 text-center">
          <div className="eyebrow">Score de préparation</div>
          <div className="font-mono text-3xl text-stone-900 mt-1">{d.planning_score}%</div>
          <Progress value={d.planning_score} className="w-32 h-1.5 mt-2" />
        </div>
      </div>

      {ob && (
        <div className="card-lux p-6 mb-6 animate-fade-up" data-testid="onboarding-checklist">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="font-serif text-2xl text-stone-900">Bienvenue {w.names} 💍</h2>
              <p className="text-sm text-stone-500 mt-1">Votre espace est prêt. Voici quelques étapes pour bien démarrer.</p>
            </div>
            <button onClick={() => window.dispatchEvent(new Event("notreoui:open-tour"))} data-testid="replay-tour-btn"
              className="text-sm text-champagne hover:underline self-start whitespace-nowrap">
              {user?.tour_completed_at ? "Revoir la visite guidée" : "Découvrir les nouveautés"}</button>
          </div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div className="eyebrow">Bien démarrer avec NotreOui</div>
            <span className="text-sm text-stone-500" data-testid="onboarding-count">
              {ob.done} étape{ob.done > 1 ? "s" : ""} sur {ob.total} terminée{ob.done > 1 ? "s" : ""}</span>
          </div>
          <Progress value={Math.round((ob.done / ob.total) * 100)} className="h-1.5 mb-4" />
          <div className="grid sm:grid-cols-2 gap-2">
            {ob.steps.map((s) => (
              <Link key={s.key} to={s.link} data-testid={`ob-step-${s.key}`}
                className={`flex items-center gap-3 p-3 rounded-xl border text-sm transition-colors ${s.done ? "bg-sage/5 border-sage/20 text-stone-500" : "hover:bg-accent/30"}`}>
                {s.done ? <Check className="w-4 h-4 text-sage flex-shrink-0" /> : <Circle className="w-4 h-4 text-stone-300 flex-shrink-0" />}
                <span className={s.done ? "line-through decoration-stone-300" : "font-medium text-stone-800"}>{s.label}</span>
                {!s.done && <ArrowRight className="w-3.5 h-3.5 ml-auto text-stone-400 flex-shrink-0" />}
              </Link>
            ))}
          </div>
        </div>
      )}

      <PremiumBanner />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="dash-guests" label="Invités confirmés" icon={Users}
          value={`${d.guests.attending}`} sub={`${d.guests.pending} en attente · ${d.guests.total} au total`} accent="sage" />
        <StatCard testid="dash-budget" label="Budget engagé" icon={Wallet}
          value={chf(d.budget.committed, cur)} sub={`Payé ${chf(d.budget.paid, cur)} · Objectif ${chf(d.budget.target, cur)}`} accent="champagne" />
        <StatCard testid="dash-tasks" label="Tâches" icon={ListChecks}
          value={`${d.tasks.done}/${d.tasks.total}`} sub={d.tasks.overdue > 0 ? `${d.tasks.overdue} en retard` : "À jour"}
          accent={d.tasks.overdue > 0 ? "destructive" : "stone"} />
        <StatCard testid="dash-seating" label="Plan de table" icon={Armchair}
          value={`${d.seating.seated}/${d.seating.attending}`} sub={`${d.seating.unseated} à placer`} accent="stone" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {d.recommendations.length > 0 && (
            <div className="card-lux p-6" data-testid="dash-recommendations">
              <div className="flex items-center gap-2 mb-4"><Lightbulb className="w-5 h-5 text-champagne" />
                <h2 className="font-serif text-xl">Recommandations</h2></div>
              <div className="space-y-2">
                {d.recommendations.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-accent/40 text-sm text-stone-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-champagne mt-1.5 flex-shrink-0" /> {r}</div>
                ))}
              </div>
            </div>
          )}

          <div className="card-lux p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Prochains paiements</h2>
              <Link to="/app/budget"><Button variant="ghost" size="sm">Voir le budget</Button></Link></div>
            {d.upcoming_payments.length === 0 ? (
              <p className="text-sm text-stone-400 py-6 text-center">Aucun paiement à venir.</p>
            ) : (
              <div className="space-y-2">
                {d.upcoming_payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-xl border">
                    <div><div className="font-medium text-sm">{p.label}</div>
                      <div className="text-xs text-stone-400">Échéance {fdateShort(p.due_date)}</div></div>
                    <div className="font-mono text-sm">{chf(p.amount, cur)}</div></div>
                ))}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/app/guests" className="card-lux p-5 flex items-center gap-4 hover:bg-accent/30">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Users className="w-5 h-5" /></div>
              <div><div className="font-medium">Gérer les invités</div><div className="text-xs text-stone-400">Foyers, RSVP, régimes</div></div></Link>
            <Link to="/app/vendors" className="card-lux p-5 flex items-center gap-4 hover:bg-accent/30">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Store className="w-5 h-5" /></div>
              <div><div className="font-medium">Prestataires</div><div className="text-xs text-stone-400">{d.vendors.booked} réservés · {d.vendors.researching} en recherche</div></div></Link>
          </div>
        </div>

        <div className="space-y-6">
          <Link to="/app/ai" className="card-lux p-6 bg-stone-900 text-white block hover:bg-stone-800 transition-colors">
            <Sparkles className="w-6 h-6 text-champagne mb-3" />
            <h2 className="font-serif text-xl">Assistant IA</h2>
            <p className="text-sm text-stone-300 mt-2">Posez une question sur votre mariage : budget, invités, planning du jour J.</p>
            <div className="text-champagne text-sm mt-4">Ouvrir l'assistant →</div></Link>

          <div className="card-lux p-6">
            <h2 className="font-serif text-xl mb-4">Activité partagée</h2>
            {activity.length === 0 ? <p className="text-sm text-stone-400">Rien pour l'instant.</p> : (
              <div className="space-y-3">
                {activity.slice(0, 8).map((a) => (
                  <div key={a.id} className="flex items-start gap-3 text-sm">
                    <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {(a.actor_name || "?")[0]}</div>
                    <div><span className="text-stone-700"><b>{a.actor_name}</b> {a.detail}</span>
                      <div className="text-xs text-stone-400">{timeAgo(a.created_at)}</div></div></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
